import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import LocationDropdowns from "../../components/LocationDropdowns";
import Permiso from "../../router/Permiso";
import { DocumentServices } from "../../services/DocumentService";
import { RegionService } from "../../services/RegionService";
import { PerfilService } from "../../services/PerfilService";
import { UsuarioService } from "../../services/UsuarioService";
import { ClienteService } from "../../services/ClienteService";
import Preloader from "../../components/Preloader";
import { useNavigate, useParams } from "react-router-dom";
import { PostulanteService } from "../../services/PostulanteService";
import { toast } from "react-toastify";

const initialState = {
    tipoDocumento: "",
    numero: "",
    perfil: "",
    nombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    celular: "",
    email: "",
    fechaNacimiento: "",
    departamento: "",
    provincia: "",
    distrito: "",
    direccion: "",
    id_region: "",
    jefe: "",
    supervisor: "",
    id_postulante: "",
    usuario:""
};

const perfilesEquipoTrabajo = [
    "Vendedor",
    "Supervisor",
    "Callcenter",
    "Asesor",
];

function NuevoUsuario({
    token,
}) {
    const navigate = useNavigate();
    const { id } = useParams();
    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [regionesDisponibles, setRegionesDisponibles] = useState([]);
    const [loadingRegiones, setLoadingRegiones] = useState(false);
    const [jefesDisponibles, setJefesDisponibles] = useState([]);
    const [supervisoresDisponibles, setSupervisoresDisponibles] = useState([]);
    const [loadingJefes, setLoadingJefes] = useState(false);
    const [loadingSupervisores, setLoadingSupervisores] = useState(false);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [perfiles, setPerfiles] = useState([]);

    // Estado para las ubicaciones
    const [locationData, setLocationData] = useState({
        department: "",
        province: "",
        district: "",
        zone: "",
    });

    const tipoSeleccionado = perfiles.find((td) => td.id == form.perfil);

    // Función para validar que solo se ingresen números
    const validateNumericInput = (value) => {
        return value.replace(/[^0-9]/g, "");
    };

    // Función para manejar cambios en campos numéricos
    const handleNumericChange = (e) => {
        const { name, value } = e.target;
        const numericValue = validateNumericInput(value);
        setForm((prev) => ({
            ...prev,
            [name]: numericValue,
        }));
    };

    // Función para cargar las regiones disponibles
    const cargarRegiones = async () => {
        setLoadingRegiones(true);
        try {
            const data = await RegionService.fetchRegiones(token);
            setRegionesDisponibles(data);
        } catch (error) {
            console.error("Error al cargar regiones:", error);
        } finally {
            setLoadingRegiones(false);
        }
    };

    const cargarTipoDocumentos = async () => {
        setLoadingRegiones(true);
        try {
            const tipos = await DocumentServices.getDocuments(token);
            setTiposDocumentos(tipos || []);
        } catch (error) {
            console.error("Error al cargar tipos de Documentos:", error);
        } finally {
            setLoadingRegiones(false);
        }
    };

    const cargarPerfiles = async () => {
        setLoadingRegiones(true);
        try {
            const data = await PerfilService.fetchPerfiles(token);
            setPerfiles(data);
        } catch (error) {
            console.error("Error al cargar perfiles:", error);
        } finally {
            setLoadingRegiones(false);
        }
    };

    // Función para cargar jefes disponibles
    const cargarJefes = async () => {
        setLoadingJefes(true);
        try {
            const data = await UsuarioService.fetchJefes(token);
            setJefesDisponibles(data);
        } catch (error) {
            setJefesDisponibles([]);
        } finally {
            setLoadingJefes(false);
        }
    };

    const handleBackToList = () => {
        navigate("/admin/rrhh");
    };

    // Función para cargar supervisores disponibles
    const cargarSupervisores = async () => {
        setLoadingSupervisores(true);
        try {
            const data = await UsuarioService.fetchSupervisores(token);
            setSupervisoresDisponibles(data);
        } catch (error) {
            setSupervisoresDisponibles([]);
        } finally {
            setLoadingSupervisores(false);
        }
    };

    const dividirNombre = (nombreCompleto) => {
        if (!nombreCompleto || typeof nombreCompleto !== 'string') {
            return {
                nombre: '',
                apellidoPaterno: '',
                apellidoMaterno: '',
            };
        }

        const partes = nombreCompleto.trim().split(/\s+/);

        if (partes.length === 2) {
            return {
                nombre: partes[0],
                apellidoPaterno: partes[1],
                apellidoMaterno: '',
            };
        }

        if (partes.length === 3) {
            return {
                nombre: partes[0],
                apellidoPaterno: partes[1],
                apellidoMaterno: partes[2],
            };
        }

        if (partes.length >= 4) {
            return {
                nombre: partes.slice(0, 2).join(' '),
                apellidoPaterno: partes[2],
                apellidoMaterno: partes.slice(3).join(' '),
            };
        }

        // Caso por defecto
        return {
            nombre: partes[0] ?? '',
            apellidoPaterno: partes[1] ?? '',
            apellidoMaterno: partes.slice(2).join(' ') ?? '',
        };
    };


    const fetchPostulantes = async () => {
        setLoading(true);
        try {
            const postulanteData = await PostulanteService.fetchPostulanteById(token, id);

            if (postulanteData?.id_estado_postulante !== 5) {
                navigate('/admin/rrhh');
                return;
            }

            if (postulanteData) {
                const safeValue = (value) => value != null ? value : '';
                setForm({
                    ...initialState,
                    nombre: safeValue(postulanteData.nombre),
                    apellidoPaterno: safeValue(postulanteData.apellidoPaterno),
                    apellidoMaterno: safeValue(postulanteData.apellidoMaterno),
                    tipoDocumento: safeValue(postulanteData.id_tipo_documento),
                    numero: safeValue(postulanteData.numero_documento),
                    celular: safeValue(postulanteData.telefono),
                    email: safeValue(postulanteData.email),
                    fechaNacimiento: formatDate(postulanteData.fechaNacimiento),
                    id_postulante: id,
                    departamento: postulanteData?.department_id ?? '',
                    provincia: postulanteData?.province_id ?? '',
                    distrito: postulanteData?.district_id ?? '',
                    direccion: postulanteData?.direccion ?? '',
                });

                setLocationData({
                    department: postulanteData?.department_id ?? '',
                    province: postulanteData?.province_id ?? '',
                    district: postulanteData?.district_id ?? '',
                    zone: "",
                });
            }

            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response?.status === 404 && error.response?.data?.error === 'Postulante no encontrado') {
                navigate('/admin/rrhh');
                return;
            }
            console.error("Error al obtener el postulante:", error);
        }
    };


    // Función para manejar el cambio de perfil
    const handlePerfilChange = (e) => {
        const nuevoPerfil = e.target.value;
        setForm((prev) => ({
            ...prev,
            perfil: nuevoPerfil,
            // Limpiar jefe y supervisor cuando cambia el perfil
            jefe: "",
            supervisor: "",
        }));

        // Si el perfil es Jefe, cargar supervisores
        if (nuevoPerfil === "Jefe") {
            cargarSupervisores();
        }
        // Si el perfil es Supervisor, cargar jefes
        else if (nuevoPerfil === "Supervisor") {
            cargarJefes();
        }
        // Si el perfil es Vendedor, Callcenter o Asesor, cargar ambos
        else if (["Vendedor", "Callcenter", "Asesor"].includes(nuevoPerfil)) {
            cargarJefes();
            cargarSupervisores();
        }
    };

    const buscarCliente = async () => {
        try {
            if (!form.numero || form.numero.trim() === "") {
                setErrors((prev) => ({
                    ...prev,
                    numero: "Ingrese un número válido",
                }));
                return;
            }

            const cliente = await ClienteService.buscarPorDNI({ token, numero: form.numero });

            if (cliente) {
                setForm((prev) => ({
                    ...prev,
                    nombre: cliente.nombres,
                    apellidoMaterno: cliente.apellido_materno,
                    apellidoPaterno: cliente.apellido_paterno,
                }));

                setErrors((prev) => ({
                    ...prev,
                    numero: null,
                }));
            }
        } catch (err) {
            const mensaje = err.response?.data?.error || err.message || "Error al consultar el servicio";
            setErrors((prev) => ({
                ...prev,
                numero: mensaje,
            }));
        }
    };

    useEffect(() => {
        cargarRegiones();

        cargarTipoDocumentos();

        cargarPerfiles();

        // Cargar jefes y supervisores por defecto
        cargarJefes();
        cargarSupervisores();

        fetchPostulantes();
        setErrors({});
        setSuccess("");
    }, []);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: value ?? "",
        }));
    };


    // Funciones para manejar cambios de ubicación
    const handleDepartmentChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            department: value,
            province: "",
            district: "",
            zone: "",
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            departamento: value,
            provincia: "",
            distrito: "",
            region: "",
        }));
    };

    const handleProvinceChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            province: value,
            district: "",
            zone: "",
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            provincia: value,
            distrito: "",
            region: "",
        }));
    };

    const handleDistrictChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            district: value,
            zone: "",
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            distrito: value,
            region: "",
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const iso = date.toISOString();
        return iso.split('T')[0]; // devuelve YYYY-MM-DD
    };

    const handleZoneChange = (value) => {
        setLocationData((prev) => ({
            ...prev,
            zone: value,
        }));
        // Sincronizar con form
        setForm((prev) => ({
            ...prev,
            region: value,
        }));
    };

    const validate = () => {
        let errs = {};
        // Contenedor 1
        if (!form.tipoDocumento) errs.tipoDocumento = "Tipo de Documento es Requerido";
        if (!form.numero) errs.numero = "Número es Requerido";
        if (!form.perfil) errs.perfil = "Perfil es Requerido";
        if (!form.nombre) errs.nombre = " Nombre es Requerido";
        if (!form.email) errs.email = "Correo Electrónico es Requerido";
        if (!form.departamento) errs.department = " Departamento es Requerido";
        if (!form.provincia) errs.provinces = " Provincia es Requerido";
        if (!form.distrito) errs.districts = " Distrito es Requerido";

        // Validar región siempre
        if (!form.id_region) errs.region = "Región es Requerido";

        // Validar jefe y supervisor según el perfil
        // En modo edición, ser más flexibles con la validación
        if (perfilesEquipoTrabajo.includes(tipoSeleccionado?.nombre)) {
            if (tipoSeleccionado?.nombre === "Jefe") {
                // Para jefes, solo validar supervisor
                //if (!form.supervisor) errs.supervisor = "Requerido";
            } else if (tipoSeleccionado?.nombre === "Supervisor") {
                // Para supervisores, solo validar jefe
                if (!form.jefe) errs.jefe = "Jefe es Requerido";
            } else {
                if (!form.jefe) errs.jefe = "Jefe es Requerido";
                if (!form.supervisor) errs.supervisor = "Supervisor es Requerido";
            }
        }
        return errs;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);

        if (Object.keys(errs).length === 0) {
            setLoading(true);
            try {
                const userData = {
                    tipoDocumento: form.tipoDocumento,
                    documento: form.numero,
                    perfil: form.perfil,
                    nombre: form.nombre,
                    apellidoPaterno: form.apellidoPaterno,
                    apellidoMaterno: form.apellidoMaterno,
                    celular: form.celular,
                    email: form.email,
                    password: form.password,
                    fechaNacimiento: form.fechaNacimiento,
                    departamento: form.departamento,
                    provincia: form.provincia,
                    distrito: form.distrito,
                    direccion: form.direccion,
                    id_region: form.id_region,
                    jefe: form.jefe,
                    supervisor: form.supervisor,
                    id_postulante: form.id_postulante,
                    usuario:form.usuario
                };

                const savedUser = await UsuarioService.guardarUsuario({
                    token,
                    userData
                });

                toast.success('Usuario registrado correctamente');
                navigate('/admin/rrhh');

            } catch (error) {
                console.error("Error al guardar usuario:", error);
                setErrors({
                    general: error.response?.data?.error || "Error al guardar el usuario",
                });
            } finally {
                setLoading(false);
            }
        }

    };

    // Función para obtener el nombre completo de un usuario
    const getNombreCompleto = (usuario) => {
        return `${usuario.nombre || ""} ${usuario.apellidoPaterno || ""} ${usuario.apellidoMaterno || ""
            }`.trim();
    };

    return (
        <div className="ventas-admin-content">
            {loading && <Preloader />}
            <div className="breadcrumb-container">
                <span className="breadcrumb-link" onClick={handleBackToList}>
                    ← Listado de Postulantes
                </span>
                <span className="breadcrumb-current"> / Registrar Usuario</span>
            </div>
            {success && (
                <div
                    style={{
                        color: "green",
                        textAlign: "center",
                        margin: "10px 0",
                        fontWeight: 600,
                    }}
                >
                    {success}
                </div>
            )}
            {errors.general && (
                <div
                    style={{
                        color: "red",
                        textAlign: "center",
                        margin: "10px 0",
                        fontWeight: 600,
                    }}
                >
                    {errors.general}
                </div>
            )}
            <div className="dashboard-card nuevo-producto-card">
                <span className="nueva-venta-title"> Registrar Usuario</span>
                <form onSubmit={handleSave} className="nuevo-producto-form">
                    <div className="form-section">
                        <h3 className="section-title">Datos del Usuario</h3>
                        <div className="form-grid">
                            <div>
                                <label>Tipo documento*</label>
                                <select
                                    name="tipoDocumento"
                                    value={form.tipoDocumento}
                                    onChange={handleChange}

                                >
                                    <option value="">Seleccionar</option>
                                    {tiposDocumentos.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.description}
                                        </option>
                                    ))}
                                </select>
                                {errors.tipoDocumento && (
                                    <span className="error">{errors.tipoDocumento}</span>
                                )}
                            </div>
                            <div>
                                <label>Número*</label>
                                <div className="input-with-button">
                                    <input
                                        name="numero"
                                        value={form.numero}
                                        onChange={handleNumericChange}

                                        placeholder="Solo números"
                                    />
                                    {form.tipoDocumento == "1" && (
                                        <button
                                            type="button"
                                            className="search-btn-documento"
                                            title="Buscar cliente"
                                            onClick={buscarCliente}
                                        >
                                            <Search size={20} color="#555" />
                                        </button>
                                    )}
                                </div>
                                {errors.numero && (
                                    <span className="error">{errors.numero}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label>Perfil*</label>
                                <select
                                    name="perfil"
                                    value={form.perfil}
                                    onChange={handlePerfilChange}

                                >
                                    <option value="">Seleccionar</option>
                                    {perfiles.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.nombre}
                                        </option>
                                    ))}
                                </select>
                                {errors.perfil && (
                                    <span className="error">{errors.perfil}</span>
                                )}
                            </div>
                            <div>
                                <label>Nombres</label>
                                <input
                                    name="nombre"
                                    value={form.nombre}
                                    onChange={handleChange}

                                />
                                {errors.nombre && (
                                    <span className="error">{errors.nombre}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label>Apellido paterno</label>
                                <input
                                    name="apellidoPaterno"
                                    value={form.apellidoPaterno}
                                    onChange={handleChange}

                                />
                            </div>
                            <div>
                                <label>Apellido materno</label>
                                <input
                                    name="apellidoMaterno"
                                    value={form.apellidoMaterno}
                                    onChange={handleChange}

                                />
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label>Número de celular</label>
                                <input
                                    name="celular"
                                    value={form.celular}
                                    onChange={handleNumericChange}

                                    placeholder="Solo números"
                                />
                            </div>
                            <div>
                                <label>Correo electrónico</label>
                                <input
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}

                                />
                                {errors.email && <span className="error">{errors.email}</span>}
                            </div>
                        </div>
                        <div className="form-grid">
                            <div>
                                <label>Fecha de Nacimiento</label>
                                <input
                                    type="date"
                                    name="fechaNacimiento"
                                    value={form.fechaNacimiento}
                                    onChange={handleChange}

                                />
                            </div>
                             <div>
                            <label>Código Usuario</label>
                            <input
                                name="usuario"
                                value={form.usuario}
                                onChange={handleChange}

                            />
                            {errors.usuario && (
                                <span className="error">{errors.usuario}</span>
                            )}
                        </div>
                        </div>
                       
                    </div>
                    <div className="form-section">
                        <h3 className="section-title">Datos de Ubicación</h3>
                        <div style={{ gridColumn: "1 / -1" }}>

                            <LocationDropdowns
                                selectedDepartment={locationData.department}
                                selectedProvince={locationData.province}
                                selectedDistrict={locationData.district}
                                selectedZone={locationData.zone}
                                onDepartmentChange={handleDepartmentChange}
                                onProvinceChange={handleProvinceChange}
                                onDistrictChange={handleDistrictChange}
                                onZoneChange={handleZoneChange}
                                showZones={false}
                                className="location-dropdowns"
                                token={token}
                                errors={errors}
                            />
                        </div>

                        <div style={{ gridColumn: "1 / -1" }}>
                            <label>Dirección</label>
                            <textarea
                                name="direccion"
                                value={form.direccion}
                                onChange={handleChange}
                                placeholder="Ingrese la dirección completa"
                                rows={8}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    fontSize: "14px",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    outline: "none",
                                    resize: "vertical",
                                    boxSizing: "border-box",
                                    backgroundColor: "white",
                                    transition: "border-color 0.3s ease",
                                }}
                                onFocus={(e) => (e.target.style.borderColor = "#3182ce")}
                                onBlur={(e) => (e.target.style.borderColor = "#ccc")}
                            />
                        </div>
                    </div>
                    <div className="form-section">
                        <h3 className="section-title">Datos del Perfil</h3>
                        <div className="form-grid">
                            <div>
                                <label>Región</label>
                                <select
                                    name="id_region"
                                    value={form.id_region}
                                    onChange={handleChange}
                                    disabled={loadingRegiones}
                                >
                                    <option value="">Seleccionar región</option>
                                    {loadingRegiones ? (
                                        <option disabled>Cargando regiones...</option>
                                    ) : (
                                        regionesDisponibles.map((region) => (
                                            <option key={region.id} value={region.id}>
                                                {region.description}
                                            </option>
                                        ))
                                    )}
                                </select>
                                {errors.region && <span className="error">{errors.region}</span>}
                            </div>

                            {(tipoSeleccionado?.nombre === "Supervisor" ||
                                tipoSeleccionado?.nombre === "Vendedor" ||
                                tipoSeleccionado?.nombre === "Callcenter" ||
                                tipoSeleccionado?.nombre === "Asesor") && (
                                    <div>
                                        <label>Jefe</label>
                                        <select
                                            name="jefe"
                                            value={form.jefe}
                                            onChange={handleChange}
                                            disabled={loadingJefes}
                                        >
                                            <option value="">Seleccionar jefe</option>
                                            {loadingJefes ? (
                                                <option disabled>Cargando jefes...</option>
                                            ) : (
                                                jefesDisponibles.map((jefe) => (
                                                    <option key={jefe.id} value={jefe.id}>
                                                        {getNombreCompleto(jefe)} - {jefe.region}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        {errors.jefe && <span className="error">{errors.jefe}</span>}
                                    </div>
                                )}

                            {(tipoSeleccionado?.nombre === "Vendedor" ||
                                tipoSeleccionado?.nombre === "Callcenter" ||
                                tipoSeleccionado?.nombre === "Asesor") && (
                                    <div>
                                        <label>Supervisor</label>
                                        <select
                                            name="supervisor"
                                            value={form.supervisor}
                                            onChange={handleChange}
                                            disabled={loadingSupervisores}
                                        >
                                            <option value="">Seleccionar supervisor</option>
                                            {loadingSupervisores ? (
                                                <option disabled>Cargando supervisores...</option>
                                            ) : (
                                                supervisoresDisponibles.map((supervisor) => (
                                                    <option key={supervisor.id} value={supervisor.id}>
                                                        {getNombreCompleto(supervisor)} - {supervisor.region}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        {errors.supervisor && <span className="error">{errors.supervisor}</span>}
                                    </div>
                                )}

                        </div>
                    </div>

                    <div className="form-actions">

                        <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? "Registrando..." : " Registrar"}
                        </button>
                    </div>

                </form>
            </div>
        </div>

    );
}

export default NuevoUsuario;
